import React, { useState } from "react";
import Swal from "sweetalert2";

import {
  ArrowRight,
  BadgeCheck,
  BriefcaseBusiness,
  Building2,
  Check,
  ChevronDown,
  Clock3,
  FileCheck2,
  FileText,
  GraduationCap,
  Headphones,
  Menu,
  MessageCircle,
  Phone,
  ShieldCheck,
  Truck,
  UserRound,
  Users,
  X,
  Globe2,
  HeartHandshake,
  Search,
  Send,
  LockKeyhole,
} from "lucide-react";

import logo from "./../../../vid_img/logo.png";

import "./ApostilleLp.css";

/* =========================================================
   FAQ DATA
========================================================= */

const faqs = [
  [
    "What is an Apostille?",
    "An Apostille is a certificate used to authenticate a public document for use in countries that participate in the Hague Apostille Convention.",
  ],
  [
    "Which documents can be apostilled?",
    "Common examples include educational certificates, birth and marriage certificates, affidavits, powers of attorney and eligible commercial documents. The applicable pre-authentication route depends on the document.",
  ],
  [
    "How long does the apostille process take?",
    "Processing time can vary by document, pre-authentication requirement and submission route. We confirm the applicable process before taking the document forward.",
  ],
  [
    "Do I need to visit your office?",
    "Pickup and delivery options can be arranged across Bangalore, subject to the document and service requirements.",
  ],
  [
    "What is the cost of apostille?",
    "The total cost depends on document type, authentication requirements, service handling and delivery. Request a quote for your document.",
  ],
  [
    "Which countries accept apostille?",
    "Apostille is intended for use between countries participating in the Hague Apostille Convention. Destination requirements should be checked for the specific use case.",
  ],
  [
    "Can you pick up documents from Bangalore?",
    "Yes, the page is designed around Bangalore pickup and delivery support; availability can be confirmed when you submit your details.",
  ],
  [
    "Do you also provide attestation services?",
    "The page focuses on Apostille Services in Bangalore, while the service structure can also be extended to other document legalization and attestation requirements.",
  ],
];

/* =========================================================
   DOCUMENT TYPES
========================================================= */

const docs = [
  ["Degree Certificate", GraduationCap],
  ["Diploma Certificate", FileText],
  ["Marriage Certificate", HeartHandshake],
  ["Birth Certificate", Users],
  ["PCC (Police Clearance)", ShieldCheck],
  ["Commercial Documents", Building2],
];

/* =========================================================
   COUNTRIES
========================================================= */

const countries = [
  ["🇦🇪", "UAE"],
  ["🇺🇸", "USA"],
  ["🇬🇧", "UK"],
  ["🇨🇦", "Canada"],
  ["🇦🇺", "Australia"],
  ["🇩🇪", "Germany"],
  ["🇫🇷", "France"],
  ["🇳🇱", "Netherlands"],
  ["🇸🇬", "Singapore"],
  ["🇸🇦", "Saudi Arabia"],
];

/* =========================================================
   SERVICES
========================================================= */

const serviceCards = [
  [
    "Educational Documents",
    GraduationCap,
    [
      "Degree Certificates",
      "Diplomas",
      "Mark sheets",
      "Transcripts",
      "Educational Certificates",
    ],
  ],
  [
    "Personal Documents",
    UserRound,
    [
      "Birth Certificate",
      "Marriage Certificate",
      "Single Status Certificate",
      "PCC",
      "Other Personal Documents",
    ],
  ],
  [
    "Commercial Documents",
    BriefcaseBusiness,
    [
      "Company Documents",
      "Power of Attorney",
      "MOA & AOA",
      "Invoices & Agreements",
      "Other Business Documents",
    ],
  ],
];

/* =========================================================
   WHY GOODWAY
========================================================= */

const why = [
  ["Experienced Team", Users, "Years of expertise in apostille services"],
  [
    "Document Assistance",
    FileCheck2,
    "Help with requirements and verification",
  ],
  ["Bangalore Pickup", Truck, "Pickup options across Bangalore"],
  ["Transparent Process", Clock3, "Clear communication and status updates"],
  ["Multiple Destinations", Globe2, "Support for 80+ countries"],
  ["Customer Support", Headphones, "We're here when you need us"],
];

/* =========================================================
   PROCESS
========================================================= */

const steps = [
  [
    "01",
    "Share Your Details",
    FileText,
    "Tell us your document type and destination country.",
  ],
  [
    "02",
    "Document Verification",
    Search,
    "We verify the requirements and guide you on the process.",
  ],
  [
    "03",
    "Processing",
    BadgeCheck,
    "Your documents are processed through the relevant authorities.",
  ],
  [
    "04",
    "Receive Your Documents",
    Send,
    "Get your apostilled documents via pickup or delivery.",
  ],
];

/* =========================================================
   MAIN COMPONENT
========================================================= */

const ApostilleLp = () => {
  const [open, setOpen] = useState(null);
  const [menu, setMenu] = useState(false);

  return (
    <div className="apostilleLp-site">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <header className="apostilleLp-header">
        <div className="apostilleLp-container apostilleLp-nav">
          <a className="apostilleLp-logo" href="#top">
            <b>
              <img src={logo} alt="Goodway Attestation" />
            </b>
          </a>

          <button
            className="apostilleLp-menuBtn"
            onClick={() => setMenu(!menu)}
            type="button"
            aria-label="Toggle navigation"
          >
            {menu ? <X /> : <Menu />}
          </button>

          <nav className={menu ? "apostilleLp-open" : ""}>
            <a href="#services">Apostille Services</a>
            <a href="#documents">Document Types</a>
            <a href="#countries">Countries</a>
            <a href="#process">Process</a>
            <a href="#reviews">Reviews</a>
            <a href="#faq">FAQs</a>
          </nav>

          <div className="apostilleLp-navActions">
            <a className="apostilleLp-phone" href="tel:+919148889444">
              <Phone size={16} />

              <span>
                +91 9148889444
                <small>Call Us Now</small>
              </span>
            </a>

            <a
              className="apostilleLp-wa"
              href="https://wa.me/919148889444"
              target="_blank"
              rel="noreferrer"
            >
              <MessageCircle size={18} />
              WhatsApp
              <span>Chat with our Expert</span>
            </a>
          </div>
        </div>
      </header>

      <main id="top">
        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="apostilleLp-hero">
          <div className="apostilleLp-heroGlow"></div>

          <div className="apostilleLp-container apostilleLp-heroGrid">
            <div className="apostilleLp-heroCopy">
              <div className="apostilleLp-eyebrow apostilleLp-green">
                ● APOSTILLE SERVICES IN BANGALORE
              </div>

              <h1>
                Get Your Documents <em>Apostilled</em> in Bangalore
              </h1>

              <p>
                Professional apostille services for educational, personal and
                commercial documents in Bangalore. End-to-end assistance with
                document verification and apostille support.
              </p>

              <div className="apostilleLp-featureRow">
                {[
                  [Truck, "Doorstep Pickup", "Across Bangalore"],
                  [
                    FileCheck2,
                    "Expert Guidance",
                    "From verification to delivery",
                  ],
                  [Clock3, "Timely Processing", "Transparent updates"],
                  [
                    ShieldCheck,
                    "Secure Handling",
                    "Your documents are safe with us",
                  ],
                ].map(([Icon, title, text]) => (
                  <div className="apostilleLp-feature" key={title}>
                    <span>
                      <Icon />
                    </span>

                    <b>{title}</b>

                    <small>{text}</small>
                  </div>
                ))}
              </div>

              <div className="apostilleLp-trust">
                <div className="apostilleLp-google">
                  <strong>G</strong>

                  <div>
                    <b>
                      4.9/5 <span>★★★★★</span>
                    </b>

                    <small>From 2,400+ happy customers</small>
                  </div>
                </div>

                <div className="apostilleLp-avatars">
                  <i>AK</i>
                  <i>PM</i>
                  <i>RS</i>
                  <i>SK</i>

                  <span>
                    Trusted by Students,
                    <br />
                    Professionals & Businesses
                  </span>
                </div>
              </div>

              <div className="apostilleLp-heroBtns">
                <a
                  href="#quote"
                  className="apostilleLp-btn apostilleLp-primary"
                >
                  Get Free Apostille Quote
                  <ArrowRight />
                </a>

                <a
                  href="https://wa.me/919148889444"
                  target="_blank"
                  rel="noreferrer"
                  className="apostilleLp-btn apostilleLp-outline"
                >
                  <MessageCircle />
                  WhatsApp an Expert
                  <small>Quick Response</small>
                </a>
              </div>
            </div>

            <HeroArt />

            <QuoteForm />
          </div>
        </section>

        {/* =====================================================
            PROCESS
        ===================================================== */}

        <section id="process" className="apostilleLp-section apostilleLp-pale">
          <SectionTitle
            tag="SIMPLE. TRANSPARENT. HASSLE-FREE"
            title={
              <>
                How <em>Apostille</em> Works
              </>
            }
            sub="Get your documents apostilled in a simple and clear process."
          />

          <div className="apostilleLp-container apostilleLp-steps">
            {steps.map(([number, title, Icon, description], index) => (
              <React.Fragment key={number}>
                <div className="apostilleLp-step">
                  <span className="apostilleLp-num">{number}</span>

                  <div className="apostilleLp-stepIcon">
                    <Icon />
                  </div>

                  <b>{title}</b>

                  <p>{description}</p>
                </div>

                {index < steps.length - 1 && (
                  <div className="apostilleLp-arrow">→</div>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="apostilleLp-center">
            <a className="apostilleLp-btn apostilleLp-primary" href="#quote">
              Get My Free Apostille Quote
              <ArrowRight />
            </a>
          </div>
        </section>

        {/* =====================================================
            DOCUMENTS
        ===================================================== */}

        <section id="documents" className="apostilleLp-section">
          <SectionTitle
            tag="POPULAR DOCUMENTS"
            title={
              <>
                What Document Do You Need <em>Apostilled?</em>
              </>
            }
            sub="Select your document type to get started."
          />

          <div className="apostilleLp-container apostilleLp-docGrid">
            {docs.map(([title, Icon]) => (
              <a href="#quote" className="apostilleLp-docCard" key={title}>
                <span>
                  <Icon />
                </span>

                <b>{title}</b>

                <small>
                  Get Quote
                  <ArrowRight />
                </small>
              </a>
            ))}
          </div>

          <div className="apostilleLp-center apostilleLp-muted">
            Don't see your document? <a href="#quote">Talk to an Expert →</a>
          </div>
        </section>

        {/* =====================================================
            SERVICES
        ===================================================== */}

        <section id="services" className="apostilleLp-section apostilleLp-pale">
          <div className="apostilleLp-container apostilleLp-serviceLayout">
            <div className="apostilleLp-serviceIntro">
              <div className="apostilleLp-eyebrow apostilleLp-blue">
                ● OUR SERVICES
              </div>

              <h2>
                Apostille Services
                <br />
                <em>We Provide</em>
              </h2>

              <p>
                End-to-end assistance for all types of documents with guidance,
                verification and processing support.
              </p>

              <a href="#quote" className="apostilleLp-btn apostilleLp-primary">
                Get Free Quote
                <ArrowRight />
              </a>
            </div>

            <div className="apostilleLp-serviceCards">
              {serviceCards.map(([title, Icon, list]) => (
                <div className="apostilleLp-serviceCard" key={title}>
                  <Icon className="apostilleLp-serviceIcon" />

                  <h3>{title}</h3>

                  {list.map((item) => (
                    <div className="apostilleLp-check" key={item}>
                      <Check />
                      {item}
                    </div>
                  ))}

                  <a href="#quote">Get Quote →</a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================
            WHY GOODWAY
        ===================================================== */}

        <section className="apostilleLp-section">
          <SectionTitle
            tag="WHY GOODWAY"
            title={
              <>
                Why Choose <em>Goodway?</em>
              </>
            }
            sub="Reliable, Experienced, Customer-Focused."
          />

          <div className="apostilleLp-container apostilleLp-whyGrid">
            {why.map(([title, Icon, description]) => (
              <div className="apostilleLp-whyCard" key={title}>
                <span>
                  <Icon />
                </span>

                <b>{title}</b>

                <small>{description}</small>
              </div>
            ))}
          </div>
        </section>

        {/* =====================================================
            COUNTRIES
        ===================================================== */}

        <section
          id="countries"
          className="apostilleLp-section apostilleLp-pale"
        >
          <SectionTitle
            tag="GLOBAL COVERAGE"
            title={
              <>
                Apostille <em>Services</em> for Your Destination Needs
              </>
            }
            sub="We assist with apostille services for documents required in multiple countries."
          />

          <div className="apostilleLp-container apostilleLp-countryGrid">
            {countries.map(([flag, country]) => (
              <div className="apostilleLp-country" key={country}>
                <strong>{flag}</strong>

                <span>{country}</span>
              </div>
            ))}
          </div>

          <div className="apostilleLp-center">
            <a
              href="#quote"
              className="apostilleLp-btn apostilleLp-outline apostilleLp-small"
            >
              View All Countries
              <ArrowRight />
            </a>
          </div>
        </section>

        {/* =====================================================
            REVIEWS
        ===================================================== */}

        <section
          id="reviews"
          className="apostilleLp-section apostilleLp-reviews"
        >
          <SectionTitle
            tag="CUSTOMER REVIEWS"
            title={
              <>
                What Our Customers <em>Say</em>
              </>
            }
            sub="Real experiences from people who trusted Goodway."
          />

          <div className="apostilleLp-container apostilleLp-reviewGrid">
            {[
              [
                "“Very professional service. They guided me through the entire process and picked up my documents from home. Highly recommended!”",
                "Rohit S.",
                "Degree Certificate",
              ],
              [
                "“Smooth and hassle-free experience. Regular updates and quick turnaround. Great support team.”",
                "Priya M.",
                "Marriage Certificate",
              ],
              [
                "“Excellent service and customer support. Helped me with PCC apostille for Canada without any stress.”",
                "Arun K.",
                "PCC Apostille",
              ],
            ].map(([quote, name, type]) => (
              <div className="apostilleLp-review" key={name}>
                <div className="apostilleLp-stars">★★★★★</div>

                <p>{quote}</p>

                <footer>
                  <i>{name.slice(0, 2)}</i>

                  <span>
                    <b>{name}</b>
                    <small>Bangalore</small>
                  </span>

                  <small>{type}</small>
                </footer>
              </div>
            ))}
          </div>
        </section>

        {/* =====================================================
            FAQ
        ===================================================== */}

        <section id="faq" className="apostilleLp-section apostilleLp-faq">
          <SectionTitle
            tag="FREQUENTLY ASKED QUESTIONS"
            title={
              <>
                Your Questions, <em>Answered</em>
              </>
            }
            sub="Quick answers about apostille services in Bangalore."
          />

          <div className="apostilleLp-container apostilleLp-faqGrid">
            {faqs.map(([question, answer], index) => (
              <div
                className={
                  "apostilleLp-faqItem " +
                  (open === index ? "apostilleLp-active" : "")
                }
                key={question}
              >
                <button
                  type="button"
                  onClick={() => setOpen(open === index ? null : index)}
                >
                  <span>{question}</span>
                  <ChevronDown />
                </button>

                {open === index && <p>{answer}</p>}
              </div>
            ))}
          </div>
        </section>

        {/* =====================================================
            CTA
        ===================================================== */}

        <section id="quote" className="apostilleLp-cta">
          <div className="apostilleLp-container apostilleLp-ctaInner">
            <div>
              <h2>Need Apostille Services in Bangalore?</h2>

              <p>
                Tell us your document and destination country. Our team will
                guide you with the next steps.
              </p>
            </div>

            <a className="apostilleLp-btn apostilleLp-primary" href="#top">
              Get Free Apostille Quote
              <ArrowRight />
            </a>

            <a
              className="apostilleLp-btn apostilleLp-greenBtn"
              href="https://wa.me/919148889444"
              target="_blank"
              rel="noreferrer"
            >
              <MessageCircle />
              WhatsApp an Expert
            </a>

            <div className="apostilleLp-call">
              <small>Prefer to speak?</small>

              <b>+91 9148889444</b>
            </div>
          </div>
        </section>

        {/* =====================================================
            OFFICE
        ===================================================== */}

        <section className="apostilleLp-office">
          <div className="apostilleLp-container apostilleLp-officeGrid">
            <div className="apostilleLp-map">
              <iframe
                title="Goodway Attestation Bangalore Location"
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d7775.058287553612!2d77.572151!3d13.00194!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae17d588d9caef%3A0x5ba382acaf05e1bd!2sGoodway%20Attestation%20%26%20Apostille%20Services%20in%20Bangalore%20Pvt.%20Ltd!5e0!3m2!1sen!2sus!4v1719668500894!5m2!1sen!2sus"
                height="200"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="map"
              ></iframe>
            </div>

            <div>
              <div className="apostilleLp-eyebrow apostilleLp-orange">
                ● BANGALORE OFFICE
              </div>

              <h2>Our Office in Bangalore</h2>

              <p>
                <b>Goodway Attestation</b>
                <br />
                #123, 4th Floor, XYZ Business Center,
                <br />
                Koramangala, Bangalore - 560034, Karnataka
              </p>

              <a
                className="apostilleLp-btn apostilleLp-outline apostilleLp-small"
                href="#top"
              >
                Get Directions
                <ArrowRight />
              </a>
            </div>

            <div className="apostilleLp-officeMeta">
              <div>
                <Clock3 />

                <b>Working Hours</b>

                <span>
                  Mon - Sat: 9:30 AM - 6:30 PM
                  <br />
                  Sunday: Closed
                </span>
              </div>

              <div>
                <Phone />

                <b>Call Us</b>

                <span>+91 9148889444</span>
              </div>

              <div>
                <MessageCircle />

                <b>WhatsApp</b>

                <span>Chat with our team</span>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* =====================================================
          FOOTER
      ===================================================== */}

      <footer className="apostilleLp-footer">
        <div className="apostilleLp-container apostilleLp-footGrid">
          <div>
            <a className="apostilleLp-logo apostilleLp-light" href="#top">
              <img src={logo} alt="Goodway Attestation" />
            </a>

            <p>
              Your trusted partner for apostille and attestation services in
              Bangalore.
            </p>

            <div className="apostilleLp-social">
              f&nbsp;&nbsp;in&nbsp;&nbsp;◎&nbsp;&nbsp;▶
            </div>
          </div>

          <div>
            <h4>Quick Links</h4>

            <a href="#services">Apostille Services</a>
            <a href="#documents">Document Types</a>
            <a href="#countries">Countries</a>
            <a href="#process">Process</a>
            <a href="#faq">FAQs</a>
          </div>

          <div>
            <h4>Our Services</h4>

            <a href="#services">Educational Documents</a>

            <a href="#services">Personal Documents</a>

            <a href="#services">Commercial Documents</a>

            <a href="#services">Apostille Services</a>
          </div>

          <div>
            <h4>Contact Us</h4>

            <a href="tel:+919148889444">☎ +91 9148889444</a>

            <a href="mailto:info@goodwayattestation.com">
              ✉ info@goodwayattestation.com
            </a>

            <a href="#top">⌖ Koramangala, Bangalore</a>
          </div>

          <div>
            <h4>Legal</h4>

            <a href="#top">Privacy Policy</a>
            <a href="#top">Terms & Conditions</a>
            <a href="#top">Disclaimer</a>
          </div>
        </div>

        <div className="apostilleLp-container apostilleLp-copyright">
          <span>© 2026 Goodway Attestation. All rights reserved.</span>
        </div>
        {/* <div className="disclaimer">
         <p> Disclaimer: Goodway Attestation is an independent private document
          attestation and legalization service provider in Bangalore, Karnataka.
          We are not a governament authority, embassy, consulate or governament
          department and we do not represent any governament unless expressly
          stated and supposed by official authorization, Our services are
          limited to documents processing, attestation, legalization and related
          assistance. Governament requirements, fees, processing times and
          outcomes may vary depending on the document type and the concerned
          authority. We do not guarnatee visa approval, immigration approval,
          employment, admission or any governament decision.{" "}</p>
        </div> */}
      </footer>
    </div>
  );
};

/* =========================================================
   SECTION TITLE
========================================================= */

function SectionTitle({ tag, title, sub }) {
  return (
    <div className="apostilleLp-sectionTitle">
      <div className="apostilleLp-eyebrow apostilleLp-blue">● {tag}</div>

      <h2>{title}</h2>

      <p>{sub}</p>
    </div>
  );
}

/* =========================================================
   HERO ART
========================================================= */

function HeroArt() {
  return (
    <div className="apostilleLp-heroArt">
      <div className="apostilleLp-skyline">
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
      </div>

      <div className="apostilleLp-landmark">✦</div>

      <div className="apostilleLp-passport">
        PASSPORT
        <div className="apostilleLp-globe">◎</div>
      </div>

      <div className="apostilleLp-apostilleDoc">
        <div className="apostilleLp-docTop">APOSTILLE</div>

        <small>(Convention de La Haye du 5 octobre 1961)</small>

        <div className="apostilleLp-lines"></div>

        <div className="apostilleLp-seal">
          APOSTILLE
          <br />
          INDIA
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   QUOTE FORM
========================================================= */

function QuoteForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const form = event.currentTarget;

    if (isSubmitting) return;

    const formData = new FormData(form);

    formData.append("access_key", "f333e4b3-a1ed-4165-a06a-0db699456e2d");

    formData.append("subject", "New Apostille Quote Request");

    const object = Object.fromEntries(formData.entries());

    try {
      setIsSubmitting(true);

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },

        body: JSON.stringify(object),
      });

      const result = await response.json();

      if (result.success) {
        await Swal.fire({
          title: "Request Sent!",
          text: "Your apostille quote request has been submitted successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });

        form.reset();
      } else {
        Swal.fire({
          title: "Submission Failed",
          text:
            result.message || "Failed to send your request. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Apostille form submission error:", error);

      Swal.fire({
        title: "Something Went Wrong",
        text: "Unable to submit your request. Please try again later.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="apostilleLp-quoteForm" onSubmit={handleSubmit}>
      <h3>Get a Free Apostille Quote</h3>

      <p>
        Tell us about your document and destination country. Our team will guide
        you with the process, timeline and cost.
      </p>

      {/* FULL NAME */}

      <label>
        Full Name <i>*</i>
        <input
          required
          type="text"
          name="name"
          placeholder="Enter your full name"
          autoComplete="name"
        />
      </label>

      <div className="apostilleLp-two">
        {/* MOBILE NUMBER */}

        <label>
          Mobile Number <i>*</i>
          <input
            required
            type="tel"
            name="phone"
            placeholder="+91 10-digit number"
            autoComplete="tel"
            inputMode="tel"
          />
        </label>

        {/* CITY */}

        <label>
          City <i>*</i>
          <input
            required
            type="text"
            name="city"
            placeholder="Enter your city"
            autoComplete="address-level2"
          />
        </label>
      </div>

      {/* EMAIL */}

      <label>
        Email Address
        <input
          type="email"
          name="email"
          placeholder="you@example.com"
          autoComplete="email"
        />
      </label>

      {/* DOCUMENT TYPE */}

      <label>
        Document Type <i>*</i>
        <select required name="document_type" defaultValue="">
          <option value="" disabled>
            Select document type
          </option>

          <option value="Degree Certificate">Degree Certificate</option>

          <option value="Diploma Certificate">Diploma Certificate</option>

          <option value="Marriage Certificate">Marriage Certificate</option>

          <option value="Birth Certificate">Birth Certificate</option>

          <option value="PCC">PCC</option>

          <option value="Commercial Document">Commercial Document</option>

          <option value="Other">Other</option>
        </select>
      </label>

      {/* DESTINATION COUNTRY */}

      <label>
        Destination Country <i>*</i>
        <select required name="destination_country" defaultValue="">
          <option value="" disabled>
            Select country
          </option>

          {countries.map(([, country]) => (
            <option key={country} value={country}>
              {country}
            </option>
          ))}
        </select>
      </label>

      {/* SUBMIT BUTTON */}

      <button
        className="apostilleLp-btn apostilleLp-primary apostilleLp-full"
        type="submit"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          "Sending..."
        ) : (
          <>
            Get Free Quote Now
            <ArrowRight />
          </>
        )}
      </button>

      <small className="apostilleLp-secure">
        <LockKeyhole size={12} />
        Your information is secure. We never share your details.
      </small>
    </form>
  );
}

export default ApostilleLp;
